import { DOCUMENT } from '@angular/common';
import { computed, inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { SPINOFF_AUTH_CONFIG } from './spinoff-auth.config';

export interface SpinoffLoginCta {
  /**
   * true면 `href`가 앱 백엔드 OIDC redirect(풀페이지 이동, 예: "학교종이로 로그인")다.
   * false면 `href`가 앱 내부 로그인 라우트(`routerLink`로 이동)다.
   */
  readonly external: boolean;
  readonly href: string;
}

/**
 * 헤더·랜딩 CTA 등 "로그인" 버튼이 실제로 이동할 위치를 계산한다.
 *
 * `SpinoffLoginComponent.resolveInitialStep()`(spinoff-login.component.ts)과 같은 기준을
 * 재사용한다 — 로그인 옵션(methods + oidcProviders) 총합이 OIDC 리다이렉트 버튼 단 하나뿐이면
 * (예: schoolbell-e 단일화 앱들, 2026-08-13 Phase 2) 로그인 페이지의 방식 선택 화면을 거칠
 * 이유가 없으므로 그 버튼과 완전히 동일한 목적지로 곧장 이동시킨다. 옵션이 2개 이상이면
 * 사용자가 골라야 하므로 `loginPath` 라우트로 보낸다.
 *
 * OIDC 리다이렉트인 경우 `loginWithOidcProvider()`(spinoff-login.component.ts)와 동일하게
 * `return` 쿼리 파라미터에 복귀 위치를 실어 보낸다. 기본값은 클릭 시점의 현재 페이지다
 * (`router.url` — SPA 내내 값이 바뀌므로 `NavigationEnd`를 구독해 최신 경로를 반영한다).
 * `options.returnPath`를 주면 그 고정 경로를 대신 쓴다 — 랜딩 페이지의 "로그인" CTA처럼
 * "로그인 완료 후 이 페이지(마케팅 랜딩)로 돌아와 봐야 의미가 없고, 실제 로그인 후 목적지
 * (예: `/dashboard`)로 바로 들어가는 게 맞는" 경우에 쓴다(2026-08-14, 사용자 지적) — 헤더처럼
 * "지금 보던 화면으로 되돌아와야" 하는 위치에서는 옵션을 생략해 기본 동작을 유지한다.
 */
export function injectSpinoffLoginCta(options?: { returnPath?: string }): Signal<SpinoffLoginCta> {
  const config = inject(SPINOFF_AUTH_CONFIG);
  const router = inject(Router);
  // `window.location.origin`을 직접 참조하면 서버 렌더링(SSR/prerender) 시
  // `ReferenceError: window is not defined`로 컴포넌트 트리 전체가 빈 채로 렌더되는
  // 문제가 있었다(sbe-sms-user-web SEO prerender 시범 중 발견, 2026-08-26).
  // `DOCUMENT.defaultView`로 대체하면 이 워크스페이스의 Angular `platform-server`
  // 구현에서는 실제로 non-null 값을 준다(실측 확인). 다만 그 보장에 기대지 않고
  // `?? ''`로 방어값을 둔다 — 아래 `computed()`가 try/catch로 감싸져 있어 `origin`이
  // 비어 있고 `startUrl`이 상대경로인 극단적 케이스에서도 크래시 대신 안전한
  // 폴백(`loginPath`)으로 떨어진다.
  const origin = inject(DOCUMENT).defaultView?.location.origin ?? '';
  const methods = config.methods ?? [];
  const oidcProviders = config.oidcProviders ?? [];
  const loginPath = config.loginPath ?? '/auth/login';
  const returnPathOverride = options?.returnPath;

  const currentUrl = toSignal(
    router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => router.url),
    ),
    { initialValue: router.url },
  );

  if (methods.length === 0 && oidcProviders.length === 1) {
    const startUrl = oidcProviders[0].startUrl;
    return computed(() => {
      // `startUrl`은 실제로는 항상 절대 URL(예: `${apiBaseUrl}/auth/oidc/.../authorize-redirect`)이라
      // `origin`이 비어 있어도 `new URL()`이 base 없이 성공한다 — 다만 이 함수는 재사용 가능한
      // 공용 훅이라 이론상 상대 `startUrl`을 넘기는 소비처가 생기면 `origin`이 빈 문자열일 때
      // `TypeError`가 난다(Gemini 리뷰 지적, 2026-08-26). try/catch로 감싸 그 경우에도 크래시
      // 대신 앱 내부 로그인 라우트로 안전하게 폴백한다.
      try {
        const url = new URL(startUrl, origin || undefined);
        const returnTarget = returnPathOverride ?? sanitizeReturnCandidate(currentUrl(), origin);
        url.searchParams.set('return', returnTarget);
        return { external: true, href: url.toString() };
      } catch {
        return { external: false, href: loginPath };
      }
    });
  }

  return computed(() => ({ external: false, href: loginPath }));
}

/**
 * `router.url`을 그대로 `return`으로 실어 보내면, 사용자가 실패한 OIDC 콜백 화면
 * (`?code=...&state=...`가 남은 URL)에 머문 채 다시 로그인 버튼을 누를 때 그 stale
 * code/state가 그대로 로그인 완료 후 복귀 목적지가 되어 재처리 → 이미 소비된 code라 다시
 * 실패하는 무한 루프에 빠진다(sbe-schedule에서 실측). `code`+`state` 쿼리 조합은 앱이 어떤
 * callback 경로를 쓰든 항상 OIDC redirect 아티팩트이지 정상적인 복귀 목적지가 아니므로,
 * 이 조합이 보이면 무조건 안전한 기본값으로 대체한다.
 */
function sanitizeReturnCandidate(url: string, origin: string): string {
  const parsed = new URL(url, origin);
  if (parsed.searchParams.has('code') && parsed.searchParams.has('state')) {
    return '/';
  }
  return url;
}
